import React, { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import FeaturedLayout from '../layouts/FeaturedLayout'
import HabilidadesManager from '../admin/HabilidadesManager'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui'
import { useOrganization } from '../../contexts/OrganizationContext'
import { useConfirmDialog } from '../../hooks/useConfirmDialog'
import { getOrgDeportes } from '../../lib/supabase'
import { opcionesDeporte } from '../../lib/deportesOrg'

const HabilidadesPage = () => {
  const { org } = useOrganization()
  const [orgDeportes, setOrgDeportes] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState(null)
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!org?.id) { setLoading(false); return }
      setLoading(true)
      try {
        const { data } = await getOrgDeportes(org.id)
        if (cancelled) return
        const deportes = data || []
        setOrgDeportes(deportes)
        // Set primer deporte como activo por default
        if (deportes.length > 0 && !activeTab) {
          setActiveTab(deportes[0].deporte_id)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
    // activeTab intentionally omitted: solo seteamos default en carga inicial
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?.id])

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Cargando deportes...
        </div>
      )
    }

    if (orgDeportes.length === 0) {
      return (
        <div className="rounded-md border border-dashed border-input px-4 py-8 text-center text-sm text-muted-foreground">
          Esta organizacion no tiene deportes habilitados todavia. Pide al administrador de la plataforma que active al menos un deporte.
        </div>
      )
    }

    // 1 solo deporte: no hace falta tabs, mostrar directo
    const opciones = opcionesDeporte(orgDeportes)
    if (opciones.length === 1) {
      const o = opciones[0]
      return (
        <HabilidadesManager
          deporte={o.value}
          deporteLabel={o.label}
          orgId={org.id}
          confirm={confirm}
          alert={alert}
        />
      )
    }

    // 2+: usar tabs
    return (
      <Tabs value={activeTab || opciones[0].value} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start overflow-x-auto h-auto flex-wrap">
          {opciones.map((o) => (
            <TabsTrigger key={o.value} value={o.value} className="text-sm">
              {o.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {opciones.map((o) => (
          <TabsContent key={o.value} value={o.value} className="mt-4">
            <HabilidadesManager
              deporte={o.value}
              deporteLabel={o.label}
              orgId={org.id}
              confirm={confirm}
              alert={alert}
            />
          </TabsContent>
        ))}
      </Tabs>
    )
  }

  return (
    <FeaturedLayout
      title="Habilidades por deporte"
      subtitle="Define los enfoques (ofensiva, velocidad, tackleo, etc.) que se asignan a cada programa de entrenamiento. Sirven para etiquetar y buscar programas en la biblioteca."
    >
      {renderContent()}
      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default HabilidadesPage
